import debug from "debug";

const log = debug("app:del");

/**
 * Class responsible for handling deletion of objects in a 3D scene.
 * @category Experience
 * @subcategory ObjectManagement
 */

class DeleteObject {
  /**
   * Constructs a DeleteObject instance.
   * @param {Object} experience - The main application experience object.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.scene = experience.scene;

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.deleteSelectedObjects = this.deleteSelectedObjects.bind(this);
    this.deleteObjects = this.deleteObjects.bind(this);
    this.setupEventListeners();
  }

  cleanup() {
    console.log("Cleaning up DeleteObject...");

    // Remove event listeners
    this.eventHub.off("deleteKeyPressed", this.deleteSelectedObjects);
    this.eventHub.off("deleteObjects", this.deleteObjects);
    this.eventHub.off("deleteCM", this.deleteSelectedObjects);

    // Clear stored references
    this.scene = null;
    this.eventHub = null;
    this.experience = null;

    console.log("DeleteObject cleanup complete.");
}

  /**
   * Sets up event listeners for handling deletion events.
   */
  setupEventListeners() {
    this.eventHub.on("lockEvents", (lock) => {
      lock ? this.disableListeners() : this.enableListeners();
    });
    this.enableListeners();
  }

  /**
   * "globalKeyDown" - delete or backspace on selected objects
   * "deleteObjects" - event emitted when delete is initialized by other events, like reset to default, choose different part and similar
   * "deleteCM" - delete context menu, small menu that pops up when the user clicks right mouse button on part
   */

  enableListeners() {
    this.eventHub.on("deleteKeyPressed", this.deleteSelectedObjects);
    this.eventHub.on("deleteObjects", this.deleteObjects);
    this.eventHub.on("deleteCM", this.deleteSelectedObjects);
  }

  disableListeners() {
    this.eventHub.off("deleteKeyPressed", this.deleteSelectedObjects);
    this.eventHub.off("deleteObjects", this.deleteSelectedObjects);
    this.eventHub.off("deleteCM", this.deleteSelectedObjects);
  }

  handleKeyDown(event) {
    /*     if (event.key === "Delete" || event.key === "Backspace") {
      log("delete button clicked"); */
    this.deleteSelectedObjects();
    // }
  }

  /**
   * deleteObjects method accepts the xmdData in format {figures:[{},{}], others:[{},{}]} and will permanently delete objects with no UndoRedoLogs
   */

  deleteObjects(xmdData) {
    try {
      const objects = [...(xmdData.figures || []), ...(xmdData.others || [])];

      objects.forEach((obj) => {
        const objectId = obj.partData?.objectId || obj.objectId;
        if (objectId) {
          const sceneObject = this.scene.children.find(
            (child) => child.userData.objectId === objectId
          );
          if (sceneObject) {
            this.removeFromScene(sceneObject);
          }
        }
      });

      this.eventHub.emit("xmd");
    } catch (error) {
      console.error("Error in deleteObjects:", error);
    }
  }

  // Helper method to remove objects from the scene and dispose resources
  /**
   * Removes an object and its children from the scene and disposes of its resources.
   * @param {THREE.Object3D} object - The object to remove.
   */
  removeFromScene(object) {
    try {
      // Traverse all children to handle both CSS3DObjects and WebGL objects
      object.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) {
            child.geometry.dispose();
          }
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        }

        if (child.name === "CSS3DObject" && child.element) {
          if (child.element.parentNode) {
            child.element.parentNode.removeChild(child.element);
            log("Removed CSS3DObject element from DOM");
          }
        }
      });

      this.scene.remove(object);
      log("Removed object from scene:", object);
    } catch (error) {
      console.error("Error removing object from scene:", error);
    }
  }

  /**
   * Deletes the currently selected objects in the experience.
   */
  deleteSelectedObjects() {
    log("Attempting to delete selected objects from experience vault.");

    const selectedObjects = Array.from(this.experience.selectedObjects);
    log("this.selectedObjects", selectedObjects);

    if (selectedObjects.length === 0) {
      log("No objects selected to delete.");
      return;
    }
    this.xmdVault = this.experience.xmdVault;
    // Prepare the xmdData structure to be passed to deleteObjects
    const xmdData = { figures: [], others: [] };
    log("xmdVault", this.xmdVault);
    log("xmdVault", this.experience.xmdVault);
    log("xmdVault", this.experience);

    selectedObjects.forEach((object) => {
      const objectId = object.userData.objectId;
      log("objectId", objectId);
      const xmdObject = this.findXmdObjectById(objectId);

      if (xmdObject) {
        if (object.userData.mode === "textbox") {
          xmdData.others.push(xmdObject);
        } else {
          xmdData.figures.push(xmdObject);
        }
      }
    });
    log("xmdData", xmdData);
    // Call deleteObjects with the prepared xmdData
    this.deleteObjects(xmdData);
    this.logDeleteEvent(xmdData);
  }

  /**
   * Finds an XMD object in the vault by its object ID.
   * @param {string} objectId - The ID of the object to find.
   * @returns {Object|null} The found XMD object or null if not found.
   */
  findXmdObjectById(objectId) {
    let foundObject = null;
    // Search for the object in both figures and others arrays
    foundObject =
      this.xmdVault.figures.find(
        (figure) => figure.partData.objectId === objectId
      ) || this.xmdVault.others.find((other) => other.objectId === objectId);

    log("found object", foundObject);
    return foundObject;
  }

  /**
   * Logs a delete event to the event hub with the details of the deleted objects.
   * @param {Object} deletedXmdData - The data of the deleted objects.
   */
  logDeleteEvent(deletedXmdData) {
    this.eventHub.emit("logEvent", {
      eventType: "delete",
      data: deletedXmdData,
    });
  }
}

export default DeleteObject;
